
import type React from "react"
import { useState, useEffect } from "react"
import { Shield, BarChart3, Menu, X, PanelLeftClose, PanelLeftOpen, Zap, Brain, Users, LogOut } from "lucide-react"
import { Button } from "./ui/button"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { logout } from "../../auth/authActions"

interface GlobalNavigationProps {
  children: React.ReactNode
  governanceContent: React.ReactNode
  integrationsContent: React.ReactNode
  intelligenceContent: React.ReactNode
  teamContent: React.ReactNode
}

export function GlobalNavigation({
  children,
  governanceContent,
  integrationsContent,
  intelligenceContent,
  teamContent,
}: GlobalNavigationProps) {
  const [activeTab, setActiveTab] = useState("dashboard")
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Handle hash routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) // Remove the '#'

      // Extract the main tab from hash (e.g., "trackers" from "trackers&view=create")
      const mainTab = hash.split('&')[0]
      const validTabs = ["dashboard", "trackers", "integrations", "governance", "team"]

      if (validTabs.includes(mainTab)) {
        setActiveTab(mainTab)
      } else if (hash === "") {
        // Default to dashboard if no hash
        setActiveTab("dashboard")
        window.location.hash = "#dashboard"
      } else {
        // Don't override if it's an unrecognized hash - might be a valid sub-route
        console.log("Unrecognized hash:", hash)
      }
    }

    // Set initial tab based on current hash
    handleHashChange()

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange)

    return () => {
      window.removeEventListener("hashchange", handleHashChange)
    }
  }, [])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    window.location.hash = `#${tabId}`
  }

  const handleSignOut = () => {
    // Clear authentication state
    dispatch(logout())
    // Navigate to login page
    navigate("/org-eq/login")
  }

  const menuItems = [
    {
      id: "dashboard",
      label: "EQ Dashboard",
      icon: BarChart3,
    },
    {
      id: "trackers",
      label: "Trackers",
      icon: Brain,
    },
    {
      id: "integrations",
      label: "Integrations",
      icon: Zap,
    },
    {
      id: "team",
      label: "Team Settings",
      icon: Users,
    },
    {
      id: "governance",
      label: "Governance & Privacy",
      icon: Shield,
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card h-16 flex items-center px-6">
        <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="mr-4 lg:hidden">
          <Menu className="h-5 w-5" />
        </Button>
        <img src="/logos/orgeq-hexagon-logo.svg" alt="OrgEQ" className="h-14 w-auto" />
      </header>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        <aside
          className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-card border-r border-border z-50 transform transition-all duration-300 ease-in-out lg:relative lg:top-0 lg:h-[calc(100vh-4rem)] lg:transform-none ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          } ${sidebarCollapsed ? "lg:w-20" : "w-64"}`}
        >
          <div className={`${sidebarCollapsed ? "p-2" : "p-4"} h-full flex flex-col`}>
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <h2 className="font-semibold">Navigation</h2>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className={`hidden lg:flex mb-4 ${sidebarCollapsed ? "justify-center" : "justify-end"}`}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="h-8 w-8 p-0"
              >
                {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              </Button>
            </div>

            <nav className="space-y-2 flex-1">
              {menuItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      handleTabChange(item.id)
                      setSidebarOpen(false)
                    }}
                    className={`w-full flex items-center rounded-lg text-left transition-colors ${
                      sidebarCollapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-3"
                    } ${
                      activeTab === item.id
                        ? "bg-cyan-100 text-cyan-700 border border-cyan-200"
                        : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <div className={`${sidebarCollapsed ? "lg:hidden" : ""}`}>
                      <span className="font-medium">{item.label}</span>
                    </div>
                  </button>
                )
              })}
            </nav>

            <div className="mt-auto pt-4 border-t border-border">
              <button
                onClick={handleSignOut}
                className={`w-full flex items-center rounded-lg text-left transition-colors ${
                  sidebarCollapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-3"
                } hover:bg-muted text-muted-foreground hover:text-foreground`}
                title={sidebarCollapsed ? "Sign Out" : undefined}
              >
                <LogOut className="h-5 w-5 flex-shrink-0" />
                <div className={`${sidebarCollapsed ? "lg:hidden" : ""}`}>
                  <span className="font-medium">Sign Out</span>
                </div>
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-h-[calc(100vh-4rem)]">
          {activeTab === "dashboard" && children}
          {activeTab === "trackers" && <div className="container mx-auto px-6 py-8">{intelligenceContent}</div>}
          {activeTab === "integrations" && <div className="container mx-auto px-6 py-8">{integrationsContent}</div>}
          {activeTab === "team" && <div className="container mx-auto px-6 py-8">{teamContent}</div>}
          {activeTab === "governance" && <div className="container mx-auto px-6 py-8">{governanceContent}</div>}
        </main>
      </div>
    </div>
  )
}
